import { motion } from "framer-motion";
import { staggerContainer, fadeUp, scaleIn } from "../animations/variants";

const machines = [
  {
    name: "1.5 Tesla Hdxt MRI",
    brand: "GE Healthcare",
    tag: "Flagship MRI",
    color: "from-primary-600 to-sky-500",
    specs: [
      "1.5 Tesla Hdxt superconducting magnet",
      "16 Channel — high-resolution whole-body imaging",
      "Fast scan protocols — patient comfort first",
      "Advanced neuro and body MRI",
    ],
    icon: (
      <svg viewBox="0 0 80 80" className="w-full h-full" fill="none">
        <rect
          x="10"
          y="25"
          width="60"
          height="30"
          rx="15"
          stroke="white"
          strokeWidth="2.5"
        />
        <rect
          x="20"
          y="32"
          width="40"
          height="16"
          rx="8"
          stroke="white"
          strokeWidth="1.5"
        />
        <rect
          x="28"
          y="35"
          width="24"
          height="10"
          rx="2"
          fill="white"
          opacity="0.15"
        />
        <rect
          x="30"
          y="42"
          width="20"
          height="5"
          rx="2.5"
          fill="white"
          opacity="0.7"
        />
        <circle cx="40" cy="60" r="4" stroke="white" strokeWidth="2" />
        <line x1="40" y1="55" x2="40" y2="64" stroke="white" strokeWidth="2" />
        <line x1="34" y1="64" x2="46" y2="64" stroke="white" strokeWidth="2" />
      </svg>
    ),
  },
  {
    name: "GE Brivo 32",
    brand: "CE Certified Scanner",
    tag: "Advanced CT",
    color: "from-violet-600 to-purple-500",
    specs: [
      "32-slice helical CT — fastest scan with low radiation",
      "Ultra-low dose radiation for patient safety",
      "Pulmonary, trauma & emergency protocols",
      "3D reconstruction & angiography capable",
    ],
    icon: (
      <svg viewBox="0 0 80 80" className="w-full h-full" fill="none">
        <circle cx="40" cy="40" r="30" stroke="white" strokeWidth="2.5" />
        <circle
          cx="40"
          cy="40"
          r="18"
          stroke="white"
          strokeWidth="1.5"
          strokeDasharray="5 4"
        />
        <circle cx="40" cy="40" r="6" fill="white" opacity="0.3" />
        <rect
          x="28"
          y="38"
          width="24"
          height="4"
          rx="2"
          fill="white"
          opacity="0.8"
        />
        <circle cx="40" cy="36" r="3" fill="white" opacity="0.6" />
        <line
          x1="40"
          y1="10"
          x2="40"
          y2="20"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <line
          x1="40"
          y1="60"
          x2="40"
          y2="70"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <line
          x1="10"
          y1="40"
          x2="20"
          y2="40"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <line
          x1="60"
          y1="40"
          x2="70"
          y2="40"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
];

const facilities = [
  {
    emoji: "🧠",
    title: "Brain & Neuro",
    desc: "Acute stroke, tumor, epilepsy, MS protocols",
  },
  {
    emoji: "🫁",
    title: "Chest & Abdomen",
    desc: "Lung, liver, pancreas, adrenal imaging",
  },
  {
    emoji: "🦴",
    title: "MSK & Spine",
    desc: "Joint, spine, soft tissue, sports injuries",
  },
  {
    emoji: "🩸",
    title: "Vascular / MRA",
    desc: "Carotid, renal, peripheral angiography",
  },
  {
    emoji: "🫀",
    title: "Whole Body",
    desc: "Complete body screening & oncology staging",
  },
  {
    emoji: "🔬",
    title: "MRCP",
    desc: "Pancreato-Cholangiography (biliary system)",
  },
  {
    emoji: "💉",
    title: "MR Fistulogram",
    desc: "Perianal fistula tract imaging",
  },
  {
    emoji: "🧬",
    title: "MR Urography",
    desc: "Kidney, ureter & bladder evaluation",
  },
];

export default function TechnologySection() {
  return (
    <section
      id="technology"
      className="py-24 bg-gray-950 text-white overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="text-center mb-16"
        >
          <motion.p
            variants={fadeUp}
            className="text-primary-400 font-semibold text-sm uppercase tracking-widest mb-3"
          >
            Our Equipment
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="font-poppins font-bold text-4xl md:text-5xl mb-5"
          >
            State-of-the-Art{" "}
            <span className="bg-gradient-to-r from-sky-400 to-primary-400 bg-clip-text text-transparent">
              Technology
            </span>
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="text-gray-400 max-w-2xl mx-auto text-lg"
          >
            Our machines represent the pinnacle of diagnostic imaging —
            delivering speed, precision, and patient comfort simultaneously.
          </motion.p>
        </motion.div>

        {/* Machine Cards */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="grid md:grid-cols-2 gap-6 mb-16"
        >
          {machines.map((machine) => (
            <motion.div
              key={machine.name}
              variants={scaleIn}
              whileHover={{ scale: 1.02, y: -4 }}
              transition={{ duration: 0.3 }}
              className="relative rounded-3xl overflow-hidden border border-white/10 bg-white/5 backdrop-blur group"
            >
              {/* Gradient top */}
              <div className={`h-2 bg-gradient-to-r ${machine.color}`} />

              <div className="p-8 flex gap-6">
                {/* Machine icon */}
                <div
                  className={`w-24 h-24 shrink-0 rounded-2xl bg-gradient-to-br ${machine.color} p-4 shadow-xl`}
                >
                  {machine.icon}
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div
                    className={`inline-block text-xs font-semibold px-3 py-1 rounded-full bg-gradient-to-r ${machine.color} text-white mb-3`}
                  >
                    {machine.tag}
                  </div>
                  <h3 className="font-poppins font-bold text-xl text-white mb-1">
                    {machine.name}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">{machine.brand}</p>
                  <ul className="space-y-2">
                    {machine.specs.map((spec) => (
                      <li
                        key={spec}
                        className="flex items-start gap-2 text-sm text-gray-300"
                      >
                        <span className="text-sky-400 shrink-0 mt-0.5">→</span>
                        {spec}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Facilities grid */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
        >
          <motion.h3
            variants={fadeUp}
            className="text-center font-poppins font-semibold text-xl text-gray-300 mb-8"
          >
            Available Scan Protocols & Services
          </motion.h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {facilities.map((f, i) => (
              <motion.div
                key={f.title}
                variants={fadeUp}
                whileHover={{
                  scale: 1.05,
                  backgroundColor: "rgba(14,165,233,0.1)",
                }}
                className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center transition-all duration-300 cursor-default"
              >
                <div className="text-3xl mb-2">{f.emoji}</div>
                <div className="font-semibold text-sm text-white mb-1">
                  {f.title}
                </div>
                <div className="text-xs text-gray-500 leading-relaxed">
                  {f.desc}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* 24/7 Banner */}
        <motion.div
          variants={scaleIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-14 rounded-3xl bg-gradient-to-r from-primary-600 to-sky-500 p-8 sm:p-10 text-center relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.12),transparent)]" />
          <div className="relative">
            <div className="text-5xl font-poppins font-black mb-2">24/7</div>
            <div className="text-xl font-semibold mb-3">
              Emergency Imaging Available
            </div>
            <p className="text-white/80 mb-6">
              Round-the-clock CT & MRI for emergency cases — no appointment
              needed for urgent scans.
            </p>
            <a
              href="tel:+916301195478"
              className="inline-flex items-center gap-2 bg-white text-primary-700 font-bold px-8 py-3 rounded-full hover:bg-sky-50 transition-colors shadow-xl"
            >
              📞 Call Emergency: +91 63011 95478
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
